// CONTRACT:C3-CAPABILITY-PLUGIN.1.0
/** The chat-message model. A chat message IS a CanonicalEnvelope
 *  (CONTRACT:P1-CANONICAL-ENVELOPE.1.0) of `kind: "message"` — the only payload
 *  shape a capability may put on the bus. Rooms map to missions
 *  (envelope.security.mission), so room isolation rides the existing security
 *  labels. The body is E2E-protected: it lives in payload.encrypted_ref (an opaque
 *  TDF blob ref), NEVER in payload.clear. The clear shell carries only
 *  non-sensitive routing metadata (room, author, ordering) so an unauthorized
 *  reader gets the SHELL but not the BODY. */

import type { CanonicalEnvelope, SecurityLabels } from "../../schema/ts/envelope.ts";
import type { ChatKeyAccessPort } from "./keyaccess.ts";
// THE ONE canonical classification ladder (M3 fix) — shared, not re-declared locally.
import { CLASS_ORDER } from "../../abac-pdp/src/index.ts";

/** A decoded chat message — the capability's view over a message envelope. */
export interface ChatMessage {
  /** Envelope id (stable, used for de-dup). */
  id: string;
  /** Room the message belongs to (maps to a mission). */
  room: string;
  /** Author id (clear-shell metadata; routing, presence). */
  author: string;
  /** ISO timestamp; the ordering key within a room. */
  time: string;
  /** Monotonic per-room sequence the author stamped (tie-breaks equal timestamps). */
  seq: number;
  /** The cleartext body — present ONLY after a granted rewrap; otherwise undefined. */
  body?: string;
  /** True when the body could not be released (shell-only view). */
  redacted: boolean;
  /** The opaque encrypted_ref carried on the envelope. */
  encryptedRef: string;
  /** The message's OWN sealed classification (envelope security.classification); optional
   *  so existing consumers are unaffected. Post the room-CEILING flip this is EXACTLY the
   *  room class for every room message (the transport seal). */
  classification?: string;
  /** The operator's CONTENT marking (the composer pick, <= the room class per the CEILING
   *  gate), recovered from INSIDE the sealed body on a granted rewrap. Present only for a
   *  decoded wrapped body whose inner marking is a known class AT-OR-BELOW the envelope
   *  class (fail-closed display: a forged inner marking can never display ABOVE the seal).
   *  Undefined for legacy plain-string bodies, redacted shells, and bridged clear sends —
   *  the display chip then falls back to `classification` (the envelope class). */
  contentClassification?: string;
  /** The sender's DISPLAY CALLSIGN (clear-shell metadata, CLIENT-ASSERTED — a friendly
   *  name, NEVER an identity/authorization input). Stamped by the sending COP from its
   *  operator settings; round-trips the TAK wire as `__chat/@senderCallsign`. The UI
   *  renders it (falling back to `author`) and MUST treat it as unverified display data
   *  (the honest trust line: sender ids are asserted, not message-signed, today). */
  senderCallsign?: string;
  /** DEMO-TIER per-message signature block (client-asserted; rides the CLEAR shell, so it is
   *  present even on a redacted/denied row). Present when the sender signed the SEAL. Verify
   *  LAZILY in the trust card (`verifyMessageSignature`, fail-closed to "asserted") — NEVER an
   *  identity/authorization input. Undefined for legacy/unsigned messages (→ "asserted, not
   *  signed", the honest current state). See the DEMO-TIER SIGNING section below. */
  signature?: MessageSignature;
}

/** The room CLASSIFICATION rank ladder — the room class SEALS every room message and is
 *  the CEILING for the operator's content pick (L2 / §3, ceiling semantics 2026-07-06):
 *  a pick must be <= the room class on publish. The ONE shared canonical ladder. */
const CLASS_RANK = CLASS_ORDER;

/** Compare two classifications by rank. Unknown classifications rank ABOVE the ladder
 *  (fail-closed: an unrecognized class is treated as more restrictive, never less). */
export function classRank(classification: string): number {
  return CLASS_RANK[(classification ?? "").toUpperCase()] ?? Number.POSITIVE_INFINITY;
}

/** DoD portion-mark SHORT FORM for a classification (TOP_SECRET→TS, SECRET→S, …) —
 *  mirrors the CLASS_ABBR convention used by the COP surfaces. Used to suggest a
 *  higher-room name in the ROOM_CEILING error ("open <room>-TS for this traffic").
 *  An unknown class passes through as-is (never invents a mark). */
const CLASS_SHORT: Record<string, string> = {
  UNCLASSIFIED: "U",
  CUI: "CUI",
  CONFIDENTIAL: "C",
  SECRET: "S",
  TOP_SECRET: "TS",
};
export function classShort(classification: string): string {
  return CLASS_SHORT[(classification ?? "").toUpperCase()] ?? classification;
}

/** Build the SecurityLabels for a room's message. The room IS the mission, so room
 *  isolation is enforced through mission labels + the I5 policy ref binding. The
 *  creator-set classification + releasability are the ROOM MARKING (L2, ceiling
 *  semantics): the class SEALS every envelope in the room (a message's content pick may
 *  sit at-or-below it, carried INSIDE the sealed body) and the releasability bounds the
 *  audience (never widened). The payload is encrypted (clear-shell leakage), so the
 *  shell is routable while the body stays sealed. */
export function roomLabels(
  room: string,
  classification = "UNCLASSIFIED",
  // Default = `[]` (public / unrestricted within the classification). A coalition tag like
  // NATO is NOT a legal defense.local releasability and threw ILLEGAL_RELEASABILITY on every
  // seal; `[]` validates + seals as release-to-everyone.
  releasability: string[] = [],
): SecurityLabels {
  return {
    classification,
    releasability,
    mission: [room],
    caveats: [],
    originator_control: false,
    policy_ref: roomPolicyRef(room),
    payload_mode: "encrypted",
    leakage_profile: "clear-shell",
  };
}

/** Deterministic per-room policy ref the TDF blob binds to. */
export function roomPolicyRef(room: string): string {
  return `policy://room/${room}`;
}

/** Sentinel author id used by presence join/leave control envelopes. */
export const PRESENCE_AUTHOR = "presence";

// ---------------------------------------------------------------------------
// THE SEALED WIRE BODY (content-marking carrier — the room-CEILING flip)
// ---------------------------------------------------------------------------
//
// The envelope (transport) marking of a room message is EXACTLY the room class — the
// Glomar pin (the clear shell carries the room name in security.mission, so an envelope
// sealed below the room class would be DELIVERED to subjects who must not learn the room
// exists). The operator's at-or-below composer pick is therefore a CONTENT marking, and
// it must ride where only a GRANTED reader can see it: INSIDE the sealed body. The body
// is a plain string at the seal port, so we wrap it in a small versioned JSON carrier;
// `decodeChatBody` unwraps on the read side and falls back to plain text for any body
// that is not a well-formed carrier (old messages, bridged clear text) — never a throw.

/** The versioned sealed-body carrier. `_capChatBody: 1` is the discriminator (a
 *  distinctive key so an ordinary JSON-looking chat message is never misparsed). */
interface SealedBodyCarrier {
  _capChatBody: 1;
  text: string;
  contentClassification?: string;
}

/** Wrap a cleartext body + the operator's content marking for sealing. With NO content
 *  marking the RAW body is sealed unchanged (byte-identical to the pre-flip shape). */
export function encodeChatBody(text: string, contentClassification?: string): string {
  if (contentClassification === undefined) return text;
  const carrier: SealedBodyCarrier = { _capChatBody: 1, text, contentClassification };
  return JSON.stringify(carrier);
}

/** Unwrap an opened (granted) body. A well-formed carrier yields its text + content
 *  marking; ANYTHING else (legacy plain string, malformed JSON, a user message that just
 *  looks like JSON) is returned verbatim as the text with NO content marking. Pure and
 *  total — never throws. */
export function decodeChatBody(raw: string): { text: string; contentClassification?: string } {
  if (typeof raw !== "string" || !raw.startsWith("{")) return { text: raw };
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      parsed !== null &&
      typeof parsed === "object" &&
      (parsed as Record<string, unknown>)._capChatBody === 1 &&
      typeof (parsed as Record<string, unknown>).text === "string"
    ) {
      const p = parsed as Record<string, unknown>;
      return {
        text: p.text as string,
        contentClassification:
          typeof p.contentClassification === "string" ? p.contentClassification : undefined,
      };
    }
  } catch {
    // Not JSON — an ordinary plain-text body.
  }
  return { text: raw };
}

/** Compose a `kind: "message"` envelope from a draft, sealing the body through the
 *  injected E2E port (R1 — the async seal is AWAITED here). The body is placed in
 *  payload.encrypted_ref; payload.clear carries ONLY shell routing metadata (room,
 *  author, seq) — never the body. The room marking (classification + releasability) is
 *  the SEAL for every room envelope (L2, ceiling semantics); the caller enforces the
 *  operator's content pick <= the room class BEFORE this and passes the (envelope-class,
 *  clamped-releasability) pair here. `draft.contentClassification` (the pick) rides
 *  INSIDE the sealed body via `encodeChatBody` — never on the clear shell. */
export async function buildMessageEnvelope(
  draft: {
    id: string;
    room: string;
    author: string;
    body: string;
    time: string;
    seq: number;
    /** The operator's content marking (<= the envelope class). When present the sealed
     *  body is the versioned carrier; when absent the raw body seals unchanged. */
    contentClassification?: string;
    /** The sender's display callsign (clear-shell, client-asserted; see ChatMessage). */
    senderCallsign?: string;
  },
  keyAccess: ChatKeyAccessPort,
  classification = "UNCLASSIFIED",
  // Default = `[]` (public / unrestricted) — NATO is not a legal defense.local releasability
  // and threw on seal; see roomLabels.
  releasability: string[] = [],
  // DEMO-TIER PER-MESSAGE SIGNING (Guardrail 1): when a signer is injected (the COP host wires a
  // WebCrypto session signer at login), the message is signed AFTER sealing, over the SEAL
  // (encryptedRef) + id|room|sender. ADDITIVE + backward-compatible: omit it and the message is
  // simply unsigned (→ "asserted, not signed", the honest current state). The signature rides
  // the CLEAR shell so a reader can verify WHO sealed it even when DENIED the body.
  signer?: MessageSigner,
): Promise<CanonicalEnvelope> {
  const { encryptedRef } = await keyAccess.seal({
    room: draft.room,
    body: encodeChatBody(draft.body, draft.contentClassification),
    policyRef: roomPolicyRef(draft.room),
    classification,
    releasability,
  });

  // Sign the SEAL (not the cleartext) — bind the ZTDF envelope + id|room|sender so a valid
  // signature can never be re-attached to a DIFFERENT seal of the same text (Guardrail 1).
  const signature = signer
    ? await signSeal(signer, { sealMaterial: encryptedRef, id: draft.id, room: draft.room, sender: draft.author })
    : undefined;

  return {
    id: draft.id,
    schema: "scop.envelope/1.0",
    kind: "message",
    time: draft.time,
    valid_time: { start: draft.time, stale: draft.time },
    geometry: null,
    // Clear shell: routing metadata only. NO body here.
    properties: {
      name: `chat:${draft.room}`,
      type: "chat-message",
      source: draft.author,
      room: draft.room,
      author: draft.author,
      seq: draft.seq,
      // Display callsign (clear-shell, client-asserted) — the TAK egress reads it onto
      // __chat/@senderCallsign and the inbound decode restores it (round-trip stable).
      ...(draft.senderCallsign ? { senderCallsign: draft.senderCallsign } : {}),
      // DEMO-TIER signature block (clear-shell, client-asserted) — additive, like senderCallsign.
      ...(signature ? { signature } : {}),
    },
    security: roomLabels(draft.room, classification, releasability),
    provenance: {
      originator: draft.author,
      adapter: "cap-chat",
      received_at: draft.time,
      chain: [],
    },
    // The body is sealed: ONLY the opaque ref travels.
    payload: { encrypted_ref: encryptedRef },
  };
}

/** The clear-shell `properties.type` tag that marks a chat-ROOM DESCRIPTOR envelope —
 *  a zero-body `kind:"message"` whose `security` IS the room's marking. Published on
 *  room create / first-observe so a cleared user can list a room with no messages yet,
 *  and so the room marking is authoritative before the first message (§4.1). It flows
 *  through the SAME PDP-interposed bus as a message, so the two-state PDP decides it:
 *  permit → the room appears; deny → HIDDEN (no row, no count, no trace — Glomar). */
const ROOM_DESCRIPTOR_TYPE = "chat-room";

/** Build a chat-room DESCRIPTOR envelope. `security` IS the room marking (creator-set
 *  classification + releasability per L2, `mission:[room]`, clear-shell). It carries NO
 *  protected body — the descriptor is pure clear-shell routing metadata; its whole job
 *  is to ride the PDP seam so a denied room is absent for an uncleared viewer (§4.1). */
export function buildRoomDescriptorEnvelope(
  descriptor: {
    room: string;
    displayName?: string;
    parent?: string;
    mode?: string;
    creator: string;
    time: string;
  },
  classification = "UNCLASSIFIED",
  // Default = `[]` (public / unrestricted) — NATO is not a legal defense.local releasability
  // and threw on seal; see roomLabels.
  releasability: string[] = [],
): CanonicalEnvelope {
  const { room, creator, time } = descriptor;
  return {
    id: `chat-room:${room}`,
    schema: "scop.envelope/1.0",
    kind: "message",
    time,
    valid_time: { start: time, stale: time },
    geometry: null,
    properties: {
      name: `chat-room:${room}`,
      type: ROOM_DESCRIPTOR_TYPE,
      source: creator,
      room,
      author: creator,
      displayName: descriptor.displayName ?? room,
      parent: descriptor.parent,
      mode: descriptor.mode,
    },
    security: roomLabels(room, classification, releasability),
    provenance: { originator: creator, adapter: "cap-chat", received_at: time, chain: [] },
    // A descriptor carries NO protected body — it exists only so the room is listable.
    payload: { clear: { descriptor: true } },
  };
}

/** Is this envelope a chat-ROOM DESCRIPTOR (vs a chat message)? */
export function isRoomDescriptor(env: CanonicalEnvelope): boolean {
  return env.kind === "message" && env.properties.type === ROOM_DESCRIPTOR_TYPE;
}

/** Is this envelope a chat message this capability owns? */
export function isChatMessage(env: CanonicalEnvelope): boolean {
  return env.kind === "message" && env.properties.type === "chat-message";
}

/** The room an envelope belongs to (from the clear shell). */
export function envelopeRoom(env: CanonicalEnvelope): string | undefined {
  const room = env.properties.room;
  return typeof room === "string" ? room : env.security.mission[0];
}

/** Decode an envelope into a ChatMessage, attempting an E2E rewrap+open for the
 *  given subject (R1 — async: AWAIT rewrap, then AWAIT open ONLY on grant). On a
 *  granted rewrap the body is recovered; on deny (or no subject) the body stays
 *  undefined and `redacted` is true — the SHELL view. A deny NEVER calls open(), so a
 *  denied body can never be coerced into plaintext. */
export async function decodeMessage(
  env: CanonicalEnvelope,
  keyAccess: ChatKeyAccessPort,
  subject?: Record<string, unknown>,
): Promise<ChatMessage> {
  const room = envelopeRoom(env) ?? "";
  const author = typeof env.properties.author === "string" ? env.properties.author : env.provenance.originator;
  const seq = typeof env.properties.seq === "number" ? env.properties.seq : 0;
  const encryptedRef = env.payload.encrypted_ref ?? "";
  // Display callsign (clear-shell, client-asserted — display-only, never an identity input).
  const senderCallsign =
    typeof env.properties.senderCallsign === "string" && env.properties.senderCallsign.trim()
      ? env.properties.senderCallsign.trim()
      : undefined;
  // DEMO-TIER signature block (clear-shell, client-asserted). Defensive decode: a malformed block
  // is dropped to `undefined` (→ "asserted, not signed"). Verification is LAZY (the trust card
  // runs verifyMessageSignature over the seal-bound input); never trusted as an identity input.
  const signature = parseSignatureBlock(env.properties.signature);

  let body: string | undefined;
  let redacted = true;
  let contentClassification: string | undefined;

  if (subject && encryptedRef) {
    const decision = await keyAccess.rewrap({ encryptedRef, subject });
    if (decision.granted) {
      // open() is called ONLY on the grant branch — a deny carries no key + no plaintext.
      const opened = await keyAccess.open(encryptedRef, decision.key);
      // Unwrap the sealed-body carrier (the content-marking ride, room-CEILING flip). A
      // legacy plain-string body passes through verbatim with NO content marking.
      const decoded = decodeChatBody(opened);
      body = decoded.text;
      // FAIL-CLOSED DISPLAY: adopt the inner content marking ONLY when it is a known-ladder
      // class AT-OR-BELOW the envelope (seal) class — a forged/garbled inner marking can
      // never display ABOVE the seal (classRank(unknown) = +Infinity ⇒ dropped).
      if (
        decoded.contentClassification !== undefined &&
        classRank(decoded.contentClassification) <= classRank(env.security.classification)
      ) {
        contentClassification = decoded.contentClassification;
      }
      redacted = false;
    }
  } else if (!encryptedRef && env.security?.payload_mode === "clear") {
    // BRIDGED inbound (legacy ATAK → COP): an UNSEALED b-t-f GeoChat carries its body in the
    // clear on the wire (services/tak-gateway cotChatToEnvelope). Render it ONLY when the envelope
    // is EXPLICITLY clear (payload_mode === "clear") AND carries a string clear body. FAIL-CLOSED:
    // a sealed message has an encryptedRef and never reaches this branch, so a sealed/denied body
    // can never be coerced into plaintext here.
    const clearText = env.payload.clear?.text;
    if (typeof clearText === "string") {
      body = clearText;
      redacted = false;
    }
  }

  return { id: env.id, room, author, time: env.time, seq, body, redacted, encryptedRef, classification: env.security?.classification ?? "UNCLASSIFIED", contentClassification, senderCallsign, signature };
}

/** The total-order comparator within a room: by time, then per-room seq, then id. The
 *  SINGLE source of truth for message order — `orderMessages` and the read-state unread
 *  fold (ChatCapability.unreadCount/markRoomRead) both compare against it so "newer than the
 *  read mark" can never disagree with the displayed order. Pure; -1/0/+1. */
export function compareMessages(a: ChatMessage, b: ChatMessage): number {
  if (a.time !== b.time) return a.time < b.time ? -1 : 1;
  if (a.seq !== b.seq) return a.seq - b.seq;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/** Stable ordering within a room: by time, then per-room seq, then id. Returns a
 *  NEW sorted array (does not mutate the input). */
export function orderMessages(msgs: ChatMessage[]): ChatMessage[] {
  return [...msgs].sort(compareMessages);
}

// ---------------------------------------------------------------------------
// DIRECT MESSAGE (DM) ROOM KEYS (UX-only — a DM is a room with one other person)
// ---------------------------------------------------------------------------
//
// A direct message is modelled as an ordinary room (mission) whose key encodes the two
// participant ids, order-INDEPENDENT, so alice→bob and bob→alice converge on the SAME
// room. This is a pure naming convention over `openRoom` — it adds NO new crypto and NO
// new policy axis. A DM's TRANSPORT privacy (per-pair releasability so only the two peers
// can open it) is the separate multi-level KV-with-ABAC AOA, NOT this bundle: a v1 DM is a
// functional 1:1 channel at the room floor, not a cryptographically-private side channel.
// The display name resolves PER VIEWER (each peer shows the OTHER id), so the shared
// descriptor never has to carry a per-viewer name.

/** The canonical DM room-key prefix. */
export const DM_ROOM_PREFIX = "dm:";

/** Canonical, order-independent room key for a 1:1 DM between two ids: `dm:<a>~<b>` with
 *  the two ids SORTED, so both peers derive the identical room (mission). */
export function dmRoomKey(a: string, b: string): string {
  return `${DM_ROOM_PREFIX}${[a, b].sort().join("~")}`;
}

/** Is this room key a DM (the `dm:` convention)? Display-only bucketing — never a gate. */
export function isDmRoom(room: string): boolean {
  return room.startsWith(DM_ROOM_PREFIX);
}

/** The OTHER participant in a DM room, from the acting subject's view (the non-self token).
 *  Returns undefined for a non-DM room; for a degenerate self-DM returns the sole id. */
export function dmPeer(room: string, self: string): string | undefined {
  if (!isDmRoom(room)) return undefined;
  const ids = room.slice(DM_ROOM_PREFIX.length).split("~");
  return ids.find((id) => id !== self) ?? ids[0];
}

// ---------------------------------------------------------------------------
// ENCRYPTION-CAPABILITY DETECTION (C5, §5.1 — the A∪C hybrid)
// ---------------------------------------------------------------------------
//
// A TDF-capable client advertises a `<__tdfcap v="1" alg="ZTDF"/>` custom presence
// detail in EVERY SA presence beacon (mirrors the `<__tdf>`/`<marking>` double-underscore
// custom-detail convention; legacy clients ignore unknown details). In the COP's envelope
// model the SA presence the roster consumes is a `kind:"message"` presence-control
// envelope (PRESENCE_AUTHOR); the flag rides on `properties.tdfcap` so it shares the
// CONTACT'S OWN stale window (`valid_time.stale`) — there is no separate freshness clock.
//
// Detection is a PURE PARSE of the presence stream + PURE SELECTORS over it. A contact is
// `capable` ONLY on a FRESH, well-formed flag; ABSENCE / STALE / MALFORMED ⇒ NOT capable
// (Option C: unknown-until-proven, the flag is the proof). Fresh-decision-every-time: the
// selectors read off the LIVE roster snapshot the caller passes and re-derive on every call
// — never a cached/sticky bit (mirrors the I5 rewrap rule, §5.1).

/** The well-formed shape of the `<__tdfcap>` custom presence detail. v1 is a presence-of
 *  -flag signal: `v:"1"` and a non-empty `alg` (e.g. "ZTDF"). A malformed/partial shape is
 *  treated as ABSENT (legacy), never normalized to "capable" (§5.5 fail-closed). */
export interface TdfCapFlag {
  /** Protocol version of the capability advertisement. v1 requires `"1"`. */
  v: string;
  /** Advertised seal algorithm (e.g. "ZTDF"). Non-empty for a well-formed flag. */
  alg: string;
}

/** A per-contact capability verdict derived from the live presence stream.
 *   - `"capable"` = a FRESH, non-stale presence carries a WELL-FORMED `<__tdfcap>`.
 *   - `"legacy"`  = a FRESH, non-stale presence with NO / MALFORMED flag (the proven
 *                   degradation case — the ONLY state in which the bridge is reachable).
 *   - `"unknown"` = STALE / no live presence — capability cannot be asserted (glyph `?`).
 *                   A stale contact is NOT counted as legacy-present (it may be gone), so it
 *                   can NEVER make the bridge reachable — fail-closed toward sealing. */
export type ChatCapabilityVerdict = "capable" | "legacy" | "unknown";

/** A roster contact as the capability selectors see it — the minimal projection of a
 *  presence beacon. Derived by `contactFromPresence` off the SA presence envelope the
 *  roster already consumes; the selectors are PURE over this snapshot. */
export interface RosterContact {
  /** Contact uid / callsign (the presence event key). */
  uid: string;
  /** The room (mission) the presence is scoped to. */
  room: string;
  /** The contact's DISPLAY CALLSIGN when the presence beacon carried one (client-asserted
   *  display data — powers the DM picker / message-row display, never a gate). */
  callsign?: string;
  /** The parsed `<__tdfcap>` flag if the beacon carried a WELL-FORMED one, else undefined
   *  (absent OR malformed — both collapse to undefined ⇒ legacy when fresh). */
  tdfcap?: TdfCapFlag;
  /** ISO stale boundary of THIS presence beacon (`valid_time.stale`). The flag shares the
   *  contact's own stale window — `now > stale` ⇒ the whole contact is STALE (unknown). */
  stale: string;
}

/** Parse a raw presence custom-detail bag into a WELL-FORMED `<__tdfcap>` flag, or
 *  `undefined` if absent OR malformed. Malformed = treated as ABSENT (§5.5): a partial /
 *  wrong-typed / wrong-version detail is NEVER normalized to "capable" — the dangerous
 *  direction. v1 acceptance: `v === "1"` AND a non-empty string `alg`. */
export function parseTdfCap(raw: unknown): TdfCapFlag | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const r = raw as Record<string, unknown>;
  const v = r.v;
  const alg = r.alg;
  // STRICT: v1 only, non-empty alg. Anything else ⇒ absent (legacy), never capable.
  if (v !== "1") return undefined;
  if (typeof alg !== "string" || alg.trim().length === 0) return undefined;
  return { v: "1", alg };
}

/** Derive a `RosterContact` from a presence-control envelope (the SA presence the roster
 *  consumes). The capability flag is read off `properties.tdfcap` and parsed STRICTLY
 *  (malformed ⇒ absent ⇒ legacy). Returns `undefined` for a non-presence envelope or one
 *  with no source/room (nothing to key a contact on). */
export function contactFromPresence(env: CanonicalEnvelope): RosterContact | undefined {
  if (env.kind !== "message" || env.properties.author !== PRESENCE_AUTHOR) return undefined;
  const room = envelopeRoom(env);
  const uid = typeof env.properties.source === "string" ? env.properties.source : undefined;
  if (!room || !uid) return undefined;
  const callsign =
    typeof env.properties.callsign === "string" && env.properties.callsign.trim()
      ? env.properties.callsign.trim()
      : undefined;
  return {
    uid,
    room,
    ...(callsign ? { callsign } : {}),
    tdfcap: parseTdfCap(env.properties.tdfcap),
    // The flag shares the contact's own stale window — no separate freshness clock.
    stale: env.valid_time.stale,
  };
}

/** Is this contact's presence FRESH (still within its stale window) at `now`? `now > stale`
 *  ⇒ stale ⇒ the capability verdict is `unknown` and the contact is NOT counted as present.
 *  An unparseable stale time fails CLOSED to STALE (never treated as fresh). */
function isFresh(contact: RosterContact, now: Date): boolean {
  const staleMs = Date.parse(contact.stale);
  if (Number.isNaN(staleMs)) return false; // fail-closed: can't prove freshness ⇒ stale
  return now.getTime() <= staleMs;
}

/** PURE SELECTOR (§5.2): the capability verdict for one contact, read off the LIVE presence
 *  snapshot. FRESH + well-formed flag ⇒ `capable`; FRESH + no/malformed flag ⇒ `legacy`;
 *  STALE / no live presence ⇒ `unknown`. Re-derived every call — never cached/sticky. */
export function capabilityOf(contact: RosterContact, now: Date = new Date()): ChatCapabilityVerdict {
  if (!isFresh(contact, now)) return "unknown";
  return contact.tdfcap ? "capable" : "legacy";
}

/** PURE SELECTOR (§5.2): does this room have ANY NON-STALE participant whose `capabilityOf`
 *  is `legacy`? This is the SINGLE gate that makes the Plaintext Bridge REACHABLE (§5.3): a
 *  STALE contact is `unknown` (NOT legacy), so a contact who merely went offline can never
 *  make the bridge reachable — the operator can only bridge to a GENUINELY-PRESENT legacy
 *  client. Pure over the passed roster snapshot; re-derived every call. */
export function roomLegacyPresence(
  room: string,
  contacts: RosterContact[],
  now: Date = new Date(),
): boolean {
  return contacts.some((c) => c.room === room && capabilityOf(c, now) === "legacy");
}

/** The NON-STALE legacy callsigns currently present in a room (for the audit row + the
 *  Wave-3b confirm modal). Pure over the roster snapshot; folds off the SAME selector so the
 *  audited target set can never disagree with the reachability gate. De-duplicated. */
export function legacyCallsignsInRoom(
  room: string,
  contacts: RosterContact[],
  now: Date = new Date(),
): string[] {
  const out = new Set<string>();
  for (const c of contacts) {
    if (c.room === room && capabilityOf(c, now) === "legacy") out.add(c.uid);
  }
  return [...out];
}

// ---------------------------------------------------------------------------
// THE CLEAR (PLAINTEXT-BRIDGE) BUILD PATH (C5, §5.4)
// ---------------------------------------------------------------------------
//
// The momentary toggle chooses which BUILD PATH the SINGLE armed envelope takes:
//   - DEFAULT (not armed): `buildMessageEnvelope` (above) — the body is SEALED into
//     payload.encrypted_ref, payload_mode:"encrypted", leakage_profile:"clear-shell". This
//     is 100% of normal traffic (L3).
//   - ARMED (one-shot): `buildClearMessageEnvelope` (below) — the body rides in
//     payload.clear.text, payload_mode:"clear", leakage_profile:"clear" so a legacy client
//     can read it. This path NEVER seals — there is no encrypted_ref.
//
// CRITICAL: the clear path still binds the SAME room-marking `security` LABELS the sealed
// path would (classification / releasability / mission / policy_ref). The toggle changes ONLY
// the leakage of the BODY (sealed→clear); it does NOT lower the marking. The caller (the COP
// state machine) STILL runs the room-marking gate (the class CEILING + the releasability
// clamp) BEFORE choosing this path, so a plaintext body can never be ORIGINATED above the
// room class (§5.5, fail-closed INDEPENDENT of the toggle).

/** Build a CLEAR (un-sealed) chat envelope — the §5.4 plaintext-bridge build path. The body
 *  rides in `payload.clear.text` as PLAIN TEXT (a legacy client must read it — NO sealed-body
 *  carrier here, so a bridged message carries no content marking and the display chip falls
 *  back to the envelope class); `payload_mode:"clear"`, `leakage_profile:"clear"`. The
 *  `security` LABELS are the SAME room-marking binding the sealed path uses (classification /
 *  releasability / mission / policy_ref) — only the BODY leakage changes, never the marking.
 *  No I5 seal is performed (and none must be): the body is intentionally clear so a legacy
 *  client can read it. The caller MUST have already enforced the room-marking gate (the class
 *  CEILING + the releasability clamp, §5.5) before choosing this path. */
export function buildClearMessageEnvelope(
  draft: { id: string; room: string; author: string; body: string; time: string; seq: number; senderCallsign?: string },
  classification = "UNCLASSIFIED",
  // Default = `[]` (public / unrestricted) — NATO is not a legal defense.local releasability
  // and threw on seal; see roomLabels.
  releasability: string[] = [],
): CanonicalEnvelope {
  // SAME labels as the sealed path, then override leakage to clear (the toggle changes the
  // BODY leakage, NOT the marking — §5.5).
  const security: SecurityLabels = {
    ...roomLabels(draft.room, classification, releasability),
    payload_mode: "clear",
    leakage_profile: "clear",
  };
  return {
    id: draft.id,
    schema: "scop.envelope/1.0",
    kind: "message",
    time: draft.time,
    valid_time: { start: draft.time, stale: draft.time },
    geometry: null,
    properties: {
      name: `chat:${draft.room}`,
      type: "chat-message",
      source: draft.author,
      room: draft.room,
      author: draft.author,
      seq: draft.seq,
      ...(draft.senderCallsign ? { senderCallsign: draft.senderCallsign } : {}),
    },
    security,
    provenance: {
      originator: draft.author,
      adapter: "cap-chat",
      received_at: draft.time,
      chain: [],
    },
    // PLAINTEXT BODY — intentionally clear so a legacy client can read it. No encrypted_ref.
    payload: { clear: { text: draft.body } },
  };
}

// ---------------------------------------------------------------------------
// CLIENT-SIDE @MENTION (CONTRACT:C3-CHAT.1.0 — pure UX over the CLEAR shell, NO crypto)
// ---------------------------------------------------------------------------
//
// @mention is a NET-NEW tak-tdf feature: native ATAK GeoChat has NO @callsign mention
// (TAK-CHAT-SEMANTICS §6.4). These selectors are PURE functions over an ALREADY-DECODED
// ChatMessage body (`{author, body}`) — they read ONLY the clear shell and never the
// sealed envelope. FAIL-CLOSED BY CONSTRUCTION: a `redacted` message has `body===undefined`,
// so it parses to ZERO mentions (a sealed body the subject cannot open can never @-ping
// anyone, and a body the subject cannot read is never scanned). No new crypto, no change to
// the sealed envelope shape — v1 is in-app highlight/badge only (OS toasts deferred,
// ASYNC-BUNDLES Decisions log). Mentions resolve against the room ROSTER (presence): an
// @handle that matches no present participant highlights nothing and pings no one.
//
// HANDLE GRAMMAR (v1): `@` + `[A-Za-z0-9]` then `[A-Za-z0-9._-]*` — covers trigraph callsigns
// (`@ALPHA`) and `ANDROID-<hex>` UIDs (`@ANDROID-cafebabe`). A space is NOT part of a handle,
// so a multi-word callsign ("JOKER MAN") is NOT mentionable in v1 (flagged limitation —
// matching is exact + case-insensitive against the roster). Matching never invents an FQN or
// touches policy; it is display-only.

/** Match an `@handle` token. Global flag set on the literal so callers MUST use it with
 *  `matchAll`/`split` (never `.test`/`.exec` against the shared instance — lastIndex state). */
const MENTION_RE = /@([A-Za-z0-9][A-Za-z0-9._-]*)/g;

/** Extract the raw `@handle` tokens from a (decoded) body, de-duplicated case-insensitively,
 *  preserving the FIRST occurrence's original casing for display. A redacted/empty body
 *  (`undefined`/`""`) yields `[]` (fail-closed: a sealed body pings no one). */
export function parseMentions(body: string | undefined): string[] {
  if (!body) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const m of body.matchAll(MENTION_RE)) {
    const handle = m[1];
    const k = handle.toLowerCase();
    if (!seen.has(k)) {
      seen.add(k);
      out.push(handle);
    }
  }
  return out;
}

/** Resolve parsed mentions against a room ROSTER (the present uids/callsigns). Returns the
 *  roster uids that were @-mentioned (case-insensitive), in ROSTER order. A mention matching
 *  no present participant is DROPPED — an `@typo` resolves to nothing. */
export function resolveMentions(body: string | undefined, roster: string[]): string[] {
  const mentioned = new Set(parseMentions(body).map((h) => h.toLowerCase()));
  if (mentioned.size === 0) return [];
  const out: string[] = [];
  const emitted = new Set<string>();
  for (const uid of roster) {
    const k = uid.toLowerCase();
    if (mentioned.has(k) && !emitted.has(k)) {
      emitted.add(k);
      out.push(uid);
    }
  }
  return out;
}

/** Was `subjectId` @-mentioned in this body? Resolved against the roster when one is supplied
 *  (the subject must ALSO be a present participant for the mention to "land"); with no roster
 *  a bare `@subjectId` match counts (the receiver knows it is itself present). Case-insensitive.
 *  Drives the "mentions-you" row highlight + the in-app badge (v1 — no OS toast). */
export function mentionsSubject(body: string | undefined, subjectId: string, roster?: string[]): boolean {
  if (!subjectId) return false;
  const target = subjectId.toLowerCase();
  const handles = new Set(parseMentions(body).map((h) => h.toLowerCase()));
  if (!handles.has(target)) return false;
  if (roster && !roster.some((u) => u.toLowerCase() === target)) return false;
  return true;
}

/** A render SEGMENT for the highlight pass: the body split into ordered runs, each either
 *  plain text (`mention:false`) or a resolved `@handle` token (`mention:true`). A segment is
 *  flagged `mention:true` ONLY when its `@handle` resolves to a roster member — an `@typo`
 *  (or any text) stays plain. PURE: the component maps these to `<mark>`/text spans, so the
 *  highlight DECISION is unit-tested here, not in React. Concatenating `value` reproduces the
 *  original body exactly (no character is added or dropped). */
export interface MentionSegment {
  value: string;
  mention: boolean;
}

/** Split a (decoded) body into mention/text segments for the highlight render. A redacted
 *  body (`undefined`) yields `[]` (nothing to render here — the placeholder is shown instead). */
export function tokenizeMentions(body: string | undefined, roster: string[]): MentionSegment[] {
  if (!body) return [];
  const resolvable = new Set(resolveMentions(body, roster).map((u) => u.toLowerCase()));
  const segments: MentionSegment[] = [];
  let last = 0;
  for (const m of body.matchAll(MENTION_RE)) {
    const start = m.index ?? 0;
    const token = m[0];
    const handle = m[1];
    if (start > last) segments.push({ value: body.slice(last, start), mention: false });
    segments.push({ value: token, mention: resolvable.has(handle.toLowerCase()) });
    last = start + token.length;
  }
  if (last < body.length) segments.push({ value: body.slice(last), mention: false });
  return segments;
}

// ---------------------------------------------------------------------------
// DEMO-TIER CLIENT-SIDE PER-MESSAGE SIGNING + VERIFY (CONTRACT:C3-CAPABILITY-PLUGIN.1.0)
// ---------------------------------------------------------------------------
//
// "Trust made visible" — three ADDITIVE, DISPLAY/CLIENT-CRYPTO-ONLY features. NONE of this
// touches the PDP/KAS decision path, weakens a seal, or is an identity/authorization input:
// the honest trust line stays "client-asserted" until TDFLite lands the independent
// kernel-grade verify seam. Everything here is labelled DEMO-TIER / CLIENT-VERIFIED and
// FAILS CLOSED (a failed/absent/malformed signature is NEVER shown as "verified").
//
// ⭐ GUARDRAIL 1 (TDFLite, non-negotiable): SIGN THE SEAL, NOT THE CLEARTEXT. The signature
// input binds the sealed TDF (`encryptedRef` — the whole ZTDF envelope, which contains the
// policy binding + the payload/integrity hashes) PLUS (message id | room | sender). So a
// valid signature can NEVER be re-attached to a DIFFERENT seal of the same text: a different
// seal ⇒ a different `encryptedRef` ⇒ a different signature input ⇒ verify fails. The
// signature rides the CLEAR shell (`properties.signature`, mirroring `senderCallsign`), computed
// AFTER the seal, so a reader can verify WHO sealed it + that it is intact even when DENIED the
// body. The session keypair is ephemeral-per-session (WebCrypto, non-extractable private where
// the platform allows). The FUTURE registered-key home is idplite delegation keygen
// (`tdflite delegate keygen` / RegisterDelegationPubkey, RFC 7638 kids) — NOT built here.
//
// ⭐ GUARDRAIL 2 (TDFLite, non-negotiable): VERIFY FAILS CLOSED to "unsigned", never to
// "verified". States: "signed" (present + verifies over the seal-bound input) / "asserted"
// (no/malformed signature — the honest current state) / "invalid" (present but fails). Absent
// or malformed key/sig material ⇒ "asserted", NEVER "signed".

/** The signature algorithm actually used (WebCrypto: EdDSA-Ed25519 preferred, ECDSA-P256
 *  fallback). Recorded on the block so the verifier imports the right key + verify params. */
export type SignatureAlg = "Ed25519" | "ECDSA_P256";

/** The verify verdict for a message signature (drives the trust-card badge). FAIL-CLOSED:
 *  only a present-AND-cryptographically-verified signature over the seal-bound input is
 *  "signed"; absent/malformed ⇒ "asserted"; present-but-fails ⇒ "invalid". */
export type SignatureVerifyState = "signed" | "asserted" | "invalid";

/** A ZTDF-assertion-shaped signature block that rides the CLEAR shell (`properties.signature`),
 *  the same additive way `senderCallsign` rides. CLIENT-ASSERTED, DEMO-TIER — never an identity
 *  or authorization input. Carries only what a reader needs to verify (alg/pub/sig) plus a
 *  display-only seal digest; verify TRUSTS NOTHING here except pub+sig, re-deriving the signed
 *  input from the message's OWN envelope (encryptedRef + id|room|sender). */
export interface MessageSignature {
  /** Carrier version discriminator (a distinctive shape so a stray object is never misread). */
  v: 1;
  alg: SignatureAlg;
  /** RFC 7638-shaped JWK thumbprint (base64url SHA-256 of the canonical public JWK). The
   *  future registered-key home is idplite delegation keygen (RegisterDelegationPubkey). */
  kid: string;
  /** base64url public key — Ed25519 SPKI (44 B) or P-256 raw uncompressed point (65 B). */
  pub: string;
  /** base64url signature over `messageSignaturePayload(...)`. */
  sig: string;
  /** DISPLAY-ONLY hex digest of the seal this signature covers (SHA-256 of the seal material).
   *  NOT trusted by verify (verify recomputes from the live envelope) — a UI affordance only. */
  sealDigest?: string;
}

/** The injected signer the produce path uses (the COP host builds a WebCrypto-backed one at
 *  login via `createSessionSigner`). Kept as a narrow port so this module stays free of any
 *  concrete key store — WebCrypto is a platform primitive, not the KAS seam. */
export interface MessageSigner {
  readonly alg: SignatureAlg;
  readonly kid: string;
  /** base64url public key (SPKI for Ed25519, raw for P-256). */
  readonly pub: string;
  /** Sign the exact bytes of `messageSignaturePayload(...)`. */
  sign(payload: Uint8Array): Promise<Uint8Array>;
}

/** Domain-separation tag for the signed input (prevents a signature made for one purpose from
 *  ever validating for another). */
const SIG_DOMAIN = "cap-chat/msg-sig/v1";

// -- pure byte helpers (no external deps; portable browser + node) ----------------------------

function toB64Url(bytes: Uint8Array): string {
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function fromB64Url(s: string): Uint8Array {
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/") + "===".slice((s.length + 3) % 4);
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function toHex(bytes: Uint8Array): string {
  let h = "";
  for (let i = 0; i < bytes.length; i++) h += bytes[i].toString(16).padStart(2, "0");
  return h;
}

/** Copy into a fresh ArrayBuffer so WebCrypto's `BufferSource` type is satisfied across TS/lib
 *  versions (TS 5.7+ made `Uint8Array` generic over `ArrayBufferLike`, which does not satisfy
 *  `ArrayBufferView<ArrayBuffer>`). An ArrayBuffer is unambiguously a BufferSource. */
function ab(u: Uint8Array): ArrayBuffer {
  const out = new ArrayBuffer(u.byteLength);
  new Uint8Array(out).set(u);
  return out;
}

/** The platform WebCrypto SubtleCrypto, or undefined if unavailable (fail-closed: no crypto ⇒
 *  a signature can never be claimed "signed"). */
function getSubtle(): SubtleCrypto | undefined {
  return (globalThis as { crypto?: { subtle?: SubtleCrypto } }).crypto?.subtle;
}

/** Build the CANONICAL, length-prefixed bytes that a message signature covers: the domain tag,
 *  the SEAL material (the whole `encryptedRef` — binds the policy binding + payload hash), and
 *  the id | room | sender scope. Length-prefixing each field makes the encoding injective, so no
 *  field-boundary confusion is possible (id/room/sender can't be smuggled across boundaries). */
export function messageSignaturePayload(f: {
  sealMaterial: string;
  id: string;
  room: string;
  sender: string;
}): Uint8Array {
  const enc = new TextEncoder();
  const fields = [
    enc.encode(SIG_DOMAIN),
    enc.encode(f.sealMaterial),
    enc.encode(f.id),
    enc.encode(f.room),
    enc.encode(f.sender),
  ];
  let total = 0;
  for (const b of fields) total += 4 + b.length;
  const out = new Uint8Array(total);
  const dv = new DataView(out.buffer);
  let o = 0;
  for (const b of fields) {
    dv.setUint32(o, b.length); // 4-byte big-endian length prefix
    o += 4;
    out.set(b, o);
    o += b.length;
  }
  return out;
}

/** WebCrypto params per algorithm (generate / sign+verify / public-key export format). */
function algSpec(alg: SignatureAlg): {
  keyGen: EcKeyGenParams | { name: string };
  op: EcdsaParams | { name: string };
  imp: EcKeyImportParams | { name: string };
  pubFmt: "spki" | "raw";
} {
  if (alg === "Ed25519") {
    return { keyGen: { name: "Ed25519" }, op: { name: "Ed25519" }, imp: { name: "Ed25519" }, pubFmt: "spki" };
  }
  return {
    keyGen: { name: "ECDSA", namedCurve: "P-256" },
    op: { name: "ECDSA", hash: "SHA-256" },
    imp: { name: "ECDSA", namedCurve: "P-256" },
    pubFmt: "raw",
  };
}

/** RFC 7638 JWK thumbprint (base64url SHA-256 of the canonical, minimally-membered, lexically
 *  ordered public JWK). Used as the demo `kid` — the same shape idplite will register later. */
async function jwkThumbprint(subtle: SubtleCrypto, jwk: JsonWebKey): Promise<string> {
  let canonical: string;
  if (jwk.kty === "OKP") canonical = `{"crv":"${jwk.crv}","kty":"OKP","x":"${jwk.x}"}`;
  else if (jwk.kty === "EC") canonical = `{"crv":"${jwk.crv}","kty":"EC","x":"${jwk.x}","y":"${jwk.y}"}`;
  else canonical = JSON.stringify(jwk);
  const digest = new Uint8Array(await subtle.digest("SHA-256", ab(new TextEncoder().encode(canonical))));
  return toB64Url(digest);
}

/** Generate a keypair with a NON-EXTRACTABLE private key where the platform allows exporting the
 *  public key of a non-extractable pair (node + modern Chrome do). Some browsers refuse to export
 *  a non-extractable public key; there we fall back to an extractable pair and simply NEVER export
 *  the private CryptoKey (held in the closure). Either way the private key material never leaves. */
async function makeSessionPair(
  subtle: SubtleCrypto,
  keyGen: EcKeyGenParams | { name: string },
  pubFmt: "spki" | "raw",
): Promise<{ pair: CryptoKeyPair; pubBytes: Uint8Array; jwk: JsonWebKey }> {
  let lastErr: unknown;
  for (const extractable of [false, true]) {
    try {
      const pair = (await subtle.generateKey(keyGen as AlgorithmIdentifier, extractable, [
        "sign",
        "verify",
      ])) as CryptoKeyPair;
      const pubBytes = new Uint8Array(await subtle.exportKey(pubFmt, pair.publicKey));
      const jwk = (await subtle.exportKey("jwk", pair.publicKey)) as JsonWebKey;
      return { pair, pubBytes, jwk };
    } catch (e) {
      lastErr = e; // non-extractable public export refused → retry extractable
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error("keypair generation failed");
}

/** Create an ephemeral-per-session WebCrypto signer: EdDSA-Ed25519 where available, else
 *  ECDSA-P256 (feature-detected). The COP host builds this ONCE at login and persists the
 *  public key with the session. FUTURE: the registered-key home is idplite delegation keygen. */
export async function createSessionSigner(): Promise<MessageSigner> {
  const subtle = getSubtle();
  if (!subtle) throw new Error("WebCrypto SubtleCrypto unavailable — cannot create a session signer");
  const order: SignatureAlg[] = ["Ed25519", "ECDSA_P256"];
  let lastErr: unknown;
  for (const alg of order) {
    const spec = algSpec(alg);
    try {
      const { pair, pubBytes, jwk } = await makeSessionPair(subtle, spec.keyGen, spec.pubFmt);
      const kid = await jwkThumbprint(subtle, jwk);
      const pub = toB64Url(pubBytes);
      return {
        alg,
        kid,
        pub,
        async sign(payload: Uint8Array): Promise<Uint8Array> {
          return new Uint8Array(await subtle.sign(spec.op as AlgorithmIdentifier, pair.privateKey, ab(payload)));
        },
      };
    } catch (e) {
      lastErr = e; // Ed25519 not supported on this platform → fall through to P-256
    }
  }
  throw new Error(`no WebCrypto signing algorithm available: ${String(lastErr)}`);
}

/** SHA-256 hex of a string (display-only seal digest for the trust card). */
async function sha256Hex(s: string): Promise<string | undefined> {
  const subtle = getSubtle();
  if (!subtle) return undefined;
  const digest = new Uint8Array(await subtle.digest("SHA-256", ab(new TextEncoder().encode(s))));
  return toHex(digest);
}

/** Produce the signature block for a sealed message — SIGNS THE SEAL (Guardrail 1). Called AFTER
 *  the body is sealed, over `messageSignaturePayload(encryptedRef, id, room, sender)`. */
export async function signSeal(
  signer: MessageSigner,
  f: { sealMaterial: string; id: string; room: string; sender: string },
): Promise<MessageSignature> {
  const payload = messageSignaturePayload(f);
  const sig = await signer.sign(payload);
  const sealDigest = await sha256Hex(f.sealMaterial);
  return {
    v: 1,
    alg: signer.alg,
    kid: signer.kid,
    pub: signer.pub,
    sig: toB64Url(sig),
    ...(sealDigest ? { sealDigest } : {}),
  };
}

/** Is this a WELL-FORMED signature block? A structurally-malformed block is treated as ABSENT
 *  (⇒ "asserted", never "signed") — the fail-closed decode direction (Guardrail 2). */
export function isWellFormedSignature(x: unknown): x is MessageSignature {
  if (!x || typeof x !== "object") return false;
  const s = x as Record<string, unknown>;
  return (
    s.v === 1 &&
    (s.alg === "Ed25519" || s.alg === "ECDSA_P256") &&
    typeof s.kid === "string" &&
    typeof s.pub === "string" &&
    s.pub.length > 0 &&
    typeof s.sig === "string" &&
    s.sig.length > 0
  );
}

/** Defensive decode of an untrusted `properties.signature` value into a MessageSignature, or
 *  `undefined` if absent/malformed (which the verify path then reports as "asserted"). */
export function parseSignatureBlock(raw: unknown): MessageSignature | undefined {
  if (!isWellFormedSignature(raw)) return undefined;
  const s = raw as MessageSignature;
  return {
    v: 1,
    alg: s.alg,
    kid: s.kid,
    pub: s.pub,
    sig: s.sig,
    ...(typeof s.sealDigest === "string" ? { sealDigest: s.sealDigest } : {}),
  };
}

/** PURE verify STATE MACHINE (Guardrail 2), split out so the fail-closed policy is unit-tested
 *  without crypto: no block ⇒ "asserted"; a block we couldn't cryptographically evaluate
 *  (`cryptoOk === null`, e.g. malformed key/sig bytes) ⇒ "asserted" (NEVER "signed"); a block
 *  that verified ⇒ "signed"; a block that failed ⇒ "invalid". */
export function verifyStateFromResult(hasBlock: boolean, cryptoOk: boolean | null): SignatureVerifyState {
  if (!hasBlock) return "asserted";
  if (cryptoOk === null) return "asserted";
  return cryptoOk ? "signed" : "invalid";
}

/** Client-side VERIFY of a message signature over the SEAL-BOUND input (Guardrail 2, fail-closed).
 *  Recomputes the signed input from the message's OWN envelope material (`sealMaterial` =
 *  encryptedRef, id, room, sender) — trusting NOTHING in the block except pub+sig. Any error
 *  (missing crypto, malformed key/sig) resolves to "asserted", never "signed". */
export async function verifyMessageSignature(input: {
  signature?: MessageSignature;
  sealMaterial: string;
  id: string;
  room: string;
  sender: string;
}): Promise<SignatureVerifyState> {
  const sig = input.signature;
  if (!isWellFormedSignature(sig)) return verifyStateFromResult(false, null);
  const subtle = getSubtle();
  if (!subtle) return verifyStateFromResult(false, null); // no crypto ⇒ cannot claim "signed"
  let cryptoOk: boolean | null;
  try {
    const spec = algSpec(sig.alg);
    const payload = messageSignaturePayload({
      sealMaterial: input.sealMaterial,
      id: input.id,
      room: input.room,
      sender: input.sender,
    });
    const key = await subtle.importKey(spec.pubFmt, ab(fromB64Url(sig.pub)), spec.imp as AlgorithmIdentifier, false, [
      "verify",
    ]);
    cryptoOk = await subtle.verify(spec.op as AlgorithmIdentifier, key, ab(fromB64Url(sig.sig)), ab(payload));
  } catch {
    cryptoOk = null; // malformed key/sig material ⇒ fail-closed to "asserted"
  }
  return verifyStateFromResult(true, cryptoOk);
}

// ---------------------------------------------------------------------------
// RAW ZTDF MANIFEST VIEWER (the "wow" — peek under the hood at the sealed envelope)
// ---------------------------------------------------------------------------
//
// A sealed chat body's `encryptedRef` is a `data:tdf;base64,…` — the ACTUAL ZTDF envelope
// (an OpenTDF ZIP: `0.manifest.json` stored uncompressed + `0.payload` = the ciphertext). We
// surface the RAW manifest ALREADY in the seal (keyAccess entries, policy binding,
// integrityInformation/segments, KAS url, method, assertions) — the "the fabric never sees
// what it carries" moment: the manifest describes the envelope, and the body is NOT in it in
// cleartext. This is a PURE parse (no crypto, no KAS, no decrypt) — it reads the same bytes a
// DENIED reader already holds, so even a sealed/redacted row can show its manifest.
//
// The parser is a faithful port of @opentdf/sdk's ZipReader: it finds central-directory records
// by signature, patches ZIP64 sentinel sizes/offsets from the extra field (real OpenTDF ZTDFs
// ARE zip64), re-reads the local header for the true data offset (tdf3-js writes 0 in the local
// size fields), and slices the stored manifest JSON. Compatible with real ZTDFs by construction.

/** A parsed view of a message's sealed envelope for the trust card. `kind` disambiguates the
 *  honest states; `manifest` (sealed) is the raw JSON to pretty-print; the highlights are
 *  best-effort extracts for the summary row. NEVER contains plaintext. */
export interface ZtdfManifestView {
  kind: "sealed" | "clear" | "external" | "unparseable";
  /** Honest human note for the non-sealed / error states. */
  note?: string;
  /** The raw manifest JSON (sealed kind only) — pretty-print this. */
  manifest?: Record<string, unknown>;
  /** Total bytes of the ZTDF envelope (sealed kind). */
  totalBytes?: number;
  // -- best-effort highlights (undefined if absent) --
  kasUrl?: string;
  policyBindingAlg?: string;
  policyBindingHash?: string;
  rootSignatureAlg?: string;
  encryptionAlgorithm?: string;
  segmentCount?: number;
  payloadEncrypted?: boolean;
  schemaVersion?: string;
  assertionCount?: number;
}

const ZIP_CD_SIG = 0x02014b50;
const ZIP_LFH_SIG = 0x04034b50;
const ZIP_CD_FIXED = 46;
const ZIP_LFH_FIXED = 30;

function zu16(b: Uint8Array, o: number): number {
  return b[o] | (b[o + 1] << 8);
}
function zu32(b: Uint8Array, o: number): number {
  return (b[o] | (b[o + 1] << 8) | (b[o + 2] << 16) | (b[o + 3] << 24)) >>> 0;
}
function zu64(b: Uint8Array, o: number): number {
  const lo = zu32(b, o);
  const hi = zu32(b, o + 4);
  const v = hi * 0x100000000 + lo;
  if (!Number.isSafeInteger(v)) throw new Error("zip64 value exceeds MAX_SAFE_INTEGER");
  return v;
}

interface ZipCdEntry {
  fileName: string;
  compressionMethod: number;
  uncompressedSize: number;
  localOffset: number;
}

/** Parse ONE central-directory record at offset `o` (with ZIP64 extra-field patching), or
 *  `undefined` if the fixed fields don't fit. */
function parseZipCd(b: Uint8Array, o: number): ZipCdEntry | undefined {
  if (o + ZIP_CD_FIXED > b.length) return undefined;
  const compressionMethod = zu16(b, o + 10);
  let uncompressedSize = zu32(b, o + 24);
  const fnl = zu16(b, o + 28);
  const efl = zu16(b, o + 30);
  let localOffset = zu32(b, o + 42);
  const nameStart = o + ZIP_CD_FIXED;
  if (nameStart + fnl > b.length) return undefined;
  const fileName = new TextDecoder().decode(b.subarray(nameStart, nameStart + fnl));
  // ZIP64 extended information extra field (0x0001): patch any 0xffffffff sentinels in order
  // (uncompressed, compressed, local-header offset — only the present ones are stored).
  if (efl > 0) {
    const exStart = nameStart + fnl;
    const exEnd = Math.min(exStart + efl, b.length);
    let p = exStart;
    while (p + 4 <= exEnd) {
      const hid = zu16(b, p);
      const dsize = zu16(b, p + 2);
      let q = p + 4;
      if (hid === 0x0001) {
        if (uncompressedSize === 0xffffffff) {
          uncompressedSize = zu64(b, q);
          q += 8;
        }
        // compressed size (skipped — not needed for a stored manifest)
        if (zu32(b, o + 20) === 0xffffffff) q += 8;
        if (localOffset === 0xffffffff) localOffset = zu64(b, q);
      }
      p = p + 4 + dsize;
    }
  }
  return { fileName, compressionMethod, uncompressedSize, localOffset };
}

/** Extract the ZTDF manifest (`0.manifest.json`, stored uncompressed) from raw ZIP bytes.
 *  Throws on malformed input (the caller maps that to `kind:"unparseable"`). */
function readZtdfManifest(bytes: Uint8Array): { manifest: Record<string, unknown> } {
  const entries: ZipCdEntry[] = [];
  for (let i = 0; i + ZIP_CD_FIXED <= bytes.length; i++) {
    if (zu32(bytes, i) !== ZIP_CD_SIG) continue;
    const cd = parseZipCd(bytes, i);
    if (!cd) continue;
    // Validate against the referenced local header — filters ciphertext false-positives.
    if (cd.localOffset + 4 > bytes.length || zu32(bytes, cd.localOffset) !== ZIP_LFH_SIG) continue;
    entries.push(cd);
  }
  const man =
    entries.find((e) => e.fileName === "0.manifest.json") ??
    entries.find((e) => e.fileName.endsWith("manifest.json"));
  if (!man) throw new Error("no manifest entry in ZTDF");
  if (man.compressionMethod !== 0) {
    throw new Error(`manifest is compressed (method ${man.compressionMethod}) — expected stored`);
  }
  // Re-read the LOCAL header to compute the true data offset (tdf3-js writes 0 in the local
  // size fields; only the filename/extra lengths there are reliable).
  const lfnl = zu16(bytes, man.localOffset + 26);
  const lefl = zu16(bytes, man.localOffset + 28);
  const dataStart = man.localOffset + ZIP_LFH_FIXED + lfnl + lefl;
  const json = new TextDecoder().decode(bytes.subarray(dataStart, dataStart + man.uncompressedSize));
  const parsed = JSON.parse(json) as Record<string, unknown>;
  return { manifest: parsed };
}

/** Pull the trust-card summary highlights out of a parsed ZTDF manifest (best-effort; a field
 *  absent from an older/variant manifest simply stays undefined). */
function manifestHighlights(manifest: Record<string, unknown>): Partial<ZtdfManifestView> {
  const out: Partial<ZtdfManifestView> = {};
  const ei = manifest.encryptionInformation as Record<string, unknown> | undefined;
  const payload = manifest.payload as Record<string, unknown> | undefined;
  if (typeof manifest.schemaVersion === "string") out.schemaVersion = manifest.schemaVersion;
  if (Array.isArray(manifest.assertions)) out.assertionCount = manifest.assertions.length;
  if (payload && typeof payload.isEncrypted === "boolean") out.payloadEncrypted = payload.isEncrypted;
  if (ei) {
    const ka = Array.isArray(ei.keyAccess) ? (ei.keyAccess[0] as Record<string, unknown> | undefined) : undefined;
    if (ka) {
      if (typeof ka.url === "string") out.kasUrl = ka.url;
      const pb = ka.policyBinding as Record<string, unknown> | undefined;
      if (pb) {
        if (typeof pb.alg === "string") out.policyBindingAlg = pb.alg;
        if (typeof pb.hash === "string") out.policyBindingHash = pb.hash;
      }
    }
    const method = ei.method as Record<string, unknown> | undefined;
    if (method && typeof method.algorithm === "string") out.encryptionAlgorithm = method.algorithm;
    const ii = ei.integrityInformation as Record<string, unknown> | undefined;
    if (ii) {
      const rs = ii.rootSignature as Record<string, unknown> | undefined;
      if (rs && typeof rs.alg === "string") out.rootSignatureAlg = rs.alg;
      if (Array.isArray(ii.segments)) out.segmentCount = ii.segments.length;
    }
  }
  return out;
}

/** Surface a message's sealed envelope for the trust card. PURE (no crypto/KAS/decrypt) — reads
 *  the same bytes a denied reader already holds. `encryptedRef` shapes the honest state:
 *   - `data:tdf;base64,…`   → parse the ZTDF → `kind:"sealed"` (+ raw manifest + highlights),
 *   - absent/empty          → `kind:"clear"` (a clear-bridge send — not a sealed object),
 *   - other URL ref         → `kind:"external"` (sealed but the envelope isn't inlined here),
 *   - a parse failure       → `kind:"unparseable"` (honest, never a fake manifest). */
export function parseZtdfManifest(encryptedRef: string | undefined): ZtdfManifestView {
  if (!encryptedRef) {
    return {
      kind: "clear",
      note: "clear-bridge — not a sealed object (the body rode in the clear so a legacy client could read it)",
    };
  }
  if (!encryptedRef.startsWith("data:")) {
    return {
      kind: "external",
      note: "sealed as a ZTDF, but the envelope is a reference (not inlined on this message)",
    };
  }
  try {
    const b64 = encryptedRef.slice(encryptedRef.indexOf(",") + 1);
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const { manifest } = readZtdfManifest(bytes);
    return { kind: "sealed", manifest, totalBytes: bytes.length, ...manifestHighlights(manifest) };
  } catch (e) {
    return { kind: "unparseable", note: `could not parse the sealed envelope: ${e instanceof Error ? e.message : String(e)}` };
  }
}
